import { useState } from 'react';
import {
  useAdminExtendTrialMutation,
  TrialDurationWeeks,
} from 'src/generated/graphql';

type Props = {
  organizationSlug: string;
  trialEndsAt: string | null;
  isTrialExpired: boolean;
  onExtended: () => void;
};

const EXTENSION_OPTIONS = [
  { value: TrialDurationWeeks.TwoWeeks, label: '2 weeks' },
  { value: TrialDurationWeeks.FourWeeks, label: '4 weeks' },
  { value: TrialDurationWeeks.SixWeeks, label: '6 weeks' },
  { value: TrialDurationWeeks.EightWeeks, label: '8 weeks' },
];

export const TrialExtensionSection = ({
  organizationSlug,
  trialEndsAt,
  isTrialExpired,
  onExtended,
}: Props) => {
  const [extensionWeeks, setExtensionWeeks] = useState<TrialDurationWeeks>(
    TrialDurationWeeks.TwoWeeks,
  );
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [extendTrial, { loading }] = useAdminExtendTrialMutation({
    onCompleted: (data) => {
      const org = data.adminExtendTrial;
      if (org?.trialEndsAt) {
        setSuccess(
          `Trial extended! New expiration: ${new Date(org.trialEndsAt).toLocaleDateString()}`,
        );
        setError(null);
        onExtended();
      }
    },
    onError: (err) => {
      setError(err.message);
      setSuccess(null);
    },
  });

  const handleExtend = () => {
    setError(null);
    setSuccess(null);
    extendTrial({
      variables: {
        organizationSlug,
        extensionWeeks,
      },
    });
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return 'Not set';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="bg-amber-50 border border-amber-200 rounded-md p-4 mt-4">
      <h4 className="text-sm font-semibold text-amber-800 mb-2">
        Pro Trial Management
      </h4>

      <div className="flex items-center gap-2 text-sm text-amber-700 mb-3">
        <span className="font-medium">Trial Expires:</span>
        <span className={isTrialExpired ? 'text-red-600 font-semibold' : ''}>
          {formatDate(trialEndsAt)}
          {isTrialExpired && ' (EXPIRED)'}
        </span>
      </div>

      <div className="flex items-center gap-3">
        <select
          value={extensionWeeks}
          onChange={(e) => setExtensionWeeks(e.target.value as TrialDurationWeeks)}
          className="px-3 py-1.5 border border-amber-300 rounded-md text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500 bg-white"
        >
          {EXTENSION_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              +{option.label}
            </option>
          ))}
        </select>

        <button
          onClick={handleExtend}
          disabled={loading}
          className="px-4 py-1.5 bg-amber-600 text-white rounded-md hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium"
        >
          {loading ? 'Extending...' : 'Extend Trial'}
        </button>
      </div>

      {error && (
        <p className="mt-2 text-sm text-red-600">{error}</p>
      )}

      {success && (
        <p className="mt-2 text-sm text-green-600">{success}</p>
      )}

      <p className="mt-2 text-xs text-amber-600">
        {isTrialExpired
          ? 'Extension will start from now since the trial has expired.'
          : 'Extension will be added to the current expiration date.'}
      </p>
    </div>
  );
};
